import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { GameType, ScreenNameSource, SimpleDate } from '@puzzlio/core';
import {
  withRepliesSeen,
  type CommentAttachment,
  type CommentReactionSummary,
  type CommentReply,
  type PuzzleComment,
  type RepliesSeen,
} from '@puzzlio/core/comments';
import { useFeatureFlag } from '@/app/hooks/useFeatureFlag';
import { trackCommentPosted, trackCommentReactionAdded, trackScreenNameSet } from '@/app/lib/analytics-events';
import { USER_QUERY_KEY, useUser, userQueryOptions, type MeResponse } from '@/app/providers/UserProvider';

/*
  The comments API, as react-query hooks. These go through /api/comments rather than calling the
  server actions, so the website and the app exercise the same routes.
*/

const commentsKey = (gameType: GameType, date: SimpleDate) => ['comments', gameType, date] as const;
const COMMENT_REPLIES_KEY = ['comment-replies'] as const;

/** Sends a request and returns its JSON, throwing the server's own `error` message when it refuses. */
async function api<T>(url: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'Something went wrong');
  return data as T;
}

export const useComments = (gameType: GameType, date: SimpleDate) =>
  useQuery({
    queryKey: commentsKey(gameType, date),
    queryFn: async () =>
      (await api<{ comments: PuzzleComment[] }>(`/api/comments?game=${gameType}&date=${date}`)).comments,
    // A refusal (a board this visitor can't open) won't change on a second try.
    retry: false,
    // Opening the comments asks for what's there now. Posting the first one fills the cache with just that
    // comment, before anyone has fetched the others.
    refetchOnMount: 'always',
  });

/** The signed-in account's screen name: null if it hasn't chosen one, undefined while loading. */
export const useScreenName = () => useQuery({ ...userQueryOptions, select: (data) => data.screenName }).data;

export const useSetScreenName = (source: ScreenNameSource) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (screenName: string) => api<{ screenName: string }>('/api/profile', 'PUT', { screenName }),
    onSuccess: ({ screenName }) => {
      trackScreenNameSet(source);
      queryClient.setQueryData<MeResponse>(USER_QUERY_KEY, (me) => me && { ...me, screenName });
      // Comments carry their author's name, so any already loaded are showing the old one.
      void queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const usePostComment = (gameType: GameType, date: SimpleDate) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comment: { body: string; parentId: number | null; attachment: CommentAttachment | null }) =>
      api<{ comment: PuzzleComment }>('/api/comments', 'POST', { game: gameType, date, ...comment }),
    onSuccess: async ({ comment }) => {
      trackCommentPosted(gameType, date, comment.parentId !== null, comment.attachment?.type === 'board');
      // A fetch already in flight may have read the board before this comment landed, and would
      // replace the list with one that lacks it.
      await queryClient.cancelQueries({ queryKey: commentsKey(gameType, date) });
      queryClient.setQueryData<PuzzleComment[]>(commentsKey(gameType, date), (comments) => [
        ...(comments ?? []),
        comment,
      ]);
      // Posting in a thread moves where its replies to this player start.
      void queryClient.invalidateQueries({ queryKey: COMMENT_REPLIES_KEY });
    },
  });
};

export const useDeleteComment = (gameType: GameType, date: SimpleDate) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => api(`/api/comments/${commentId}`, 'DELETE'),
    // Refetched rather than patched: whether the comment vanishes or stays as a placeholder, and
    // whether its parent goes with it, is the server's call.
    onSettled: () => queryClient.invalidateQueries({ queryKey: commentsKey(gameType, date) }),
  });
};

function toggled(reactions: CommentReactionSummary[], emoji: string, on: boolean): CommentReactionSummary[] {
  const existing = reactions.find((r) => r.emoji === emoji);
  if (existing?.mine === on || (!existing && !on)) return reactions;
  if (!existing) return [...reactions, { emoji, count: 1, mine: true }];
  return reactions
    .map((r) => (r.emoji === emoji ? { ...r, count: r.count + (on ? 1 : -1), mine: on } : r))
    .filter((r) => r.count > 0);
}

export const useSetReaction = (gameType: GameType, date: SimpleDate) => {
  const queryClient = useQueryClient();
  const queryKey = commentsKey(gameType, date);
  return useMutation({
    mutationFn: ({ commentId, emoji, on }: { commentId: number; emoji: string; on: boolean }) =>
      api(`/api/comments/${commentId}/reactions`, 'PUT', { emoji, on }),
    // Shown at once; the server's answer only matters if it refuses.
    onMutate: async ({ commentId, emoji, on }) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<PuzzleComment[]>(queryKey, (comments) =>
        comments?.map((c) => (c.id === commentId ? { ...c, reactions: toggled(c.reactions, emoji, on) } : c))
      );
    },
    onSuccess: (_data, { emoji, on }) => {
      if (on) trackCommentReactionAdded(gameType, date, emoji);
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
  });
};

export const useReportComment = (gameType: GameType, date: SimpleDate) => {
  const queryClient = useQueryClient();
  const queryKey = commentsKey(gameType, date);
  return useMutation({
    mutationFn: (commentId: number) => api(`/api/comments/${commentId}/report`, 'POST'),
    onMutate: async (commentId) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<PuzzleComment[]>(queryKey, (comments) =>
        comments?.map((c) => (c.id === commentId ? { ...c, reported: true } : c))
      );
    },
    onError: () => queryClient.invalidateQueries({ queryKey }),
  });
};

const BLOCKED_PLAYERS_KEY = ['blocked-players'] as const;

/** Blocks the author of a comment. Their comments leave every loaded board, hence the wide refetch. */
export const useBlockCommentAuthor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => api(`/api/comments/${commentId}/block`, 'POST'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments'] });
      void queryClient.invalidateQueries({ queryKey: BLOCKED_PLAYERS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMMENT_REPLIES_KEY });
    },
  });
};

/** The screen names of the players the signed-in player has blocked. */
export const useBlockedPlayers = () =>
  useQuery({
    queryKey: BLOCKED_PLAYERS_KEY,
    queryFn: async () => (await api<{ blocked: string[] }>('/api/blocks')).blocked,
  });

export const useUnblockPlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (screenName: string) => api('/api/blocks', 'DELETE', { screenName }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments'] });
      void queryClient.invalidateQueries({ queryKey: BLOCKED_PLAYERS_KEY });
      void queryClient.invalidateQueries({ queryKey: COMMENT_REPLIES_KEY });
    },
  });
};

interface CommentReplies {
  replies: CommentReply[];
  participating: boolean;
}

/**
 * Replies to the signed-in player in conversations they've joined lately, newest first. Fetched as the
 * page opens, and again as the tab comes back into view (react-query's refetch on focus) while the player
 * has posted lately: most never have, and can have no replies until they do, which asks again (see the
 * invalidations above). Never fetched for anyone comments aren't offered to.
 */
export const useCommentReplies = () => {
  const userId = useUser()?.auth?.id;
  const offered = useFeatureFlag('comment-system');
  return useQuery({
    queryKey: [...COMMENT_REPLIES_KEY, userId],
    queryFn: () => api<CommentReplies>('/api/comments/replies'),
    select: (data) => data.replies,
    enabled: !!userId && offered,
    refetchOnWindowFocus: (query) => query.state.data?.participating !== false,
  });
};

/**
 * Marks read each puzzle's replies through the time given, showing them read at once. Not a mutation
 * hook: it's often called as the page goes away, which is also why the request is `keepalive`.
 */
export function markCommentRepliesSeen(queryClient: QueryClient, seen: RepliesSeen[]) {
  void fetch('/api/comments/replies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seen }),
    keepalive: true,
  })
    .then((res) => {
      if (!res.ok) throw new Error(`Marking replies seen failed (${res.status})`);
    })
    .catch(() => queryClient.invalidateQueries({ queryKey: COMMENT_REPLIES_KEY }));
  // A fetch in flight read the replies before this, and would put them back unread.
  void queryClient.cancelQueries({ queryKey: COMMENT_REPLIES_KEY });
  queryClient.setQueriesData<CommentReplies>(
    { queryKey: COMMENT_REPLIES_KEY },
    (data) => data && { ...data, replies: withRepliesSeen(data.replies, seen) }
  );
}
